// @flow
import React, { useState, useEffect } from 'react';
import _ from 'shared/lib/gettext';
import { signInNext } from 'shared/lib/signInNext';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { fetchLogin, resetLoginError } from '~/app/actions';
import { S, A, DARK } from '~/app/ui/tokens';
import CheckIcon from './CheckIcon';

type Props = {
  login: Function,
  error: string,
  success: boolean,
  dispatchResetLoginError: Function,
  loggedIn: boolean,
  history: Object,
  redirect: Function,
  isFetching: boolean,
  classes: Object
};

const Login = (props: Props) => {
  const { loggedIn, isFetching, success, login, error, dispatchResetLoginError, classes: c } = props;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const isUrlRegex = /^https?:\/\/[^\s]+$/;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const { push } = useHistory();

  // Where to go after signing in: back to the Genesis address asked for (a preview link from an AI app,
  // a page in the editor, an AI app's approval page), never another site.
  const goNext = () => {
    const next = signInNext(new URLSearchParams(window.location.search).get('next'));
    if (next) {
      window.location.assign(next);
      return;
    }
    push('/admin');
  };

  // The admin names each tab by its screen; after signing out in the app, this one is the sign-in page.
  useEffect(() => { document.title = 'Sign in · Genesis'; }, []);

  /**
   * If user is logged in already, redirect them.
   */
  useEffect(() => {
    if (loggedIn && !isFetching) {
      goNext();
    }
  }, [loggedIn, isFetching, push]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const DELAY = 1000;

    // Successful login
    if (success) {
      setTimeout(goNext, DELAY);
    }
  }, [success, push]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (event: Event) => {
    event.preventDefault();
    if (username && password) {
      login(username, password);
    }
  }

  const handleChange = (
    event: Event & { currentTarget: HTMLInputElement },
    fieldname
  ) => {
    // Clear errors if any
    if (error) {
      dispatchResetLoginError();
    }
    const value = event.currentTarget.value;

    if (fieldname === 'username') {
      setUsername(value);
    } else if (fieldname === 'password') {
      setPassword(value);
    }
  }

  const logo = (
    <div className={c.logoWrapper}>
      <img alt="logo" src="/assets/img/logo-white.png" className={c.logo} />
    </div>
  );
  const title = (
    <div className={c.title}>
      <strong>{_('WELCOME_BACK')}!</strong> Please Sign In
    </div>
  );
  const usernameField = (
    <div className={c.field}>
      <div className={c.label}>Username</div>
      <input
        type="text"
        className={c.input}
        value={username}
        onChange={event => handleChange(event, 'username')}
      />
    </div>
  );
  const passwordField = (
    <div className={`${c.field} ${c.fieldWithBottomBorder}`}>
      <div className={c.label}>Password</div>
      <input
        type="password"
        className={c.input}
        value={password}
        onChange={event => handleChange(event, 'password')}
      />
    </div>
  );
  const submitButton = (
    <button
      type="submit"
      className={c.button}
      disabled={isFetching || success}
    >
      {success && <CheckIcon />}
      {isFetching && !success && (
        <span className={c.loadingContent}>
          <span className={c.spinner} aria-hidden="true" />
          LOGGING IN...
        </span>
      )}
      {!isFetching && !success && 'LOGIN'}
    </button>
  );
  const footer = (
    <div className={c.footer}>
      {submitButton}
      {error &&
        <span className={c.error}>
          {error.split('\n').map((line, lineIndex) => (
            <React.Fragment key={`line-${lineIndex}`}>
              {line.split(urlRegex).map((part, partIndex) => (
                isUrlRegex.test(part)
                  ? (
                    <a
                      key={`url-${lineIndex}-${partIndex}`}
                      href={part}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={c.errorLink}
                    >
                      {part}
                    </a>
                  )
                  : <React.Fragment key={`text-${lineIndex}-${partIndex}`}>{part}</React.Fragment>
              ))}
              {lineIndex < error.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </span>
      }
    </div>
  );

  return (
    <div className={c.bg}>
      <form className={c.container} onSubmit={handleSubmit}>
        {logo}
        {title}
        {usernameField}
        {passwordField}
        {footer}
        {/* For AI assistants that use the browser: where Genesis explains itself to them (shown after sign-in). */}
        <p className={c.aiNote} data-testid="login-ai-guide">
          AI assistants: Genesis has a guide for you at <a href="/llms.txt" className={c.aiNoteLink}>/llms.txt</a>. Ask the person to sign in first; never sign in for them.
        </p>
      </form>
    </div>
  );
}

const styles = {
  bg: {
    background: 'hsl(218, 20%, 24%)',
    backgroundImage: 'url(/assets/img/bg.jpg)',
    backgroundSize: 'cover',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  container: {
    width: '500px',
    minHeight: '350px',
    padding: '40px 0',
    background: 'hsl(0, 0%, 100%)',
    color: 'hsl(0, 0%, 95%)',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    boxShadow: '0px 3px 8px rgba(0,0,0,0.15)',
    [DARK]: { background: A.surface }
  },
  aiNote: {
    margin: '20px 40px 0',
    fontSize: '12px',
    lineHeight: 1.5,
    textAlign: 'center',
    color: 'hsl(219, 10%, 45%)',
    [DARK]: { color: S.muted }
  },
  aiNoteLink: { color: 'inherit', textDecoration: 'underline' },
  title: {
    fontSize: '24px',
    color: 'hsl(219, 14%, 28%)',
    fontWeight: '300',
    textAlign: 'center',
    marginBottom: '34px',
    [DARK]: { color: S.fg }
  },
  field: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid hsl(0, 0%, 85%)',
    padding: '0 20px',
    height: '65px',
    [DARK]: { borderTopColor: S.line }
  },
  fieldWithBottomBorder: {
    borderBottom: '1px solid hsl(0, 0%, 85%)',
    [DARK]: { borderBottomColor: S.line }
  },
  label: {
    fontWeight: '400',
    fontSize: '15px',
    color: 'hsl(0, 0%, 25%)',
    marginRight: '15px',
    width: '20%',
    [DARK]: { color: S.muted }
  },
  input: {
    flexGrow: '2',
    height: '100%',
    cursor: 'auto',
    border: 'none',
    background: 'none',
    outline: 'none',
    fontSize: '15px',
    color: props => props.error && 'hsl(0, 50%, 50%)' || 'hsl(0, 0%, 10%)',
    [DARK]: { color: props => (props.error ? S.red : S.fg) }
  },
  button: {
    borderRadius: '3px',
    padding: '13px 0',
    fontSize: '14px',
    fontWeight: '900',
    textTransform: 'uppercase',
    background: props => props.success && 'none' ||
      'linear-gradient(hsl(206, 59%, 48%),hsl(206, 67%, 42%))',
    color: 'hsl(0, 0%, 100%)',
    border: props => props.success && 'none' || '1px solid hsl(210, 23%, 86%)',
    [DARK]: { borderColor: S.line },
    margin: '0 40px',
    cursor: props => props.isFetching ? 'wait' : 'pointer',
    outline: 'none',
    minHeight: '44px',
    opacity: props => props.isFetching ? 0.9 : 1
  },
  footer: {
    marginTop: 'auto',
    paddingTop: '20px',
    display: 'flex',
    flexDirection: 'column'
  },
  loadingContent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  spinner: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    border: '2px solid hsla(0, 0%, 100%, 0.35)',
    borderTopColor: 'hsl(0, 0%, 100%)',
    animation: '$spin 700ms linear infinite'
  },
  '@keyframes spin': {
    from: {
      transform: 'rotate(0deg)'
    },
    to: {
      transform: 'rotate(360deg)'
    }
  },
  logoWrapper: {
    marginTop: '-65px',
    width: '50px',
    height: '50px',
    backgroundColor: 'hsl(218, 20%, 24%)',
    border: '3px solid hsl(0, 0%, 100%)',
    borderRadius: '50%',
    boxShadow: '0px 2px 15px hsla(0, 0%, 0%, .9) inset',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    padding: '5px'
  },
  logo: {

  },
  error: {
    color: 'hsl(0, 50%, 60%)',
    alignSelf: 'center',
    marginBottom: '0',
    fontSize: '12px',
    marginTop: '10px',
    maxWidth: '420px',
    textAlign: 'center',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
    [DARK]: { color: S.red }
  },
  errorLink: {
    color: 'hsl(206, 59%, 48%)',
    textDecoration: 'underline',
    [DARK]: { color: S.blue }
  }
};
const styledLogin = injectSheet(styles)(Login);

const mapStateToProps = state => ({
  error: state.app.loginError,
  success: !!state.app.user && state.app.user.isActive,
  loggedIn: !!state.app.user && state.app.user.isActive,
  isFetching: state.app.isFetchingLogin
});
const mapDispatchToProps = dispatch => ({
  login: (username, password) => dispatch(fetchLogin(username, password)),
  dispatchResetLoginError: () => dispatch(resetLoginError())
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(styledLogin);
